import React, { useState, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Alert,
  CircularProgress, Link
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { INDESAP } from '../../theme';
import DevBadge from '../../components/common/DevBadge';

export default function OTPVerification() {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const inputsRef = useRef([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const email = location.state?.email;

  if (!email) {
    navigate('/login');
    return null;
  }

  const handleChange = (idx, value) => {
    if (!/^[0-9]?$/.test(value)) return;
    const newCode = [...code];
    newCode[idx] = value;
    setCode(newCode);
    if (value && idx < 5) inputsRef.current[idx + 1]?.focus();
    if (newCode.every(d => d !== '')) {
      handleVerify(newCode.join(''));
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !code[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const newCode = pasted.split('');
      setCode(newCode);
      handleVerify(pasted);
    }
  };

  const handleVerify = async (codeStr) => {
    setError('');
    setLoading(true);
    try {
      const { data } = await authAPI.verifyOTP(email, codeStr);
      login(data.token, data.user);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Código incorrecto. Intenta de nuevo.');
      setCode(['', '', '', '', '', '']);
      inputsRef.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setSuccess('');
    setResendLoading(true);
    try {
      await authAPI.requestOTP(email);
      setSuccess('Se envió un nuevo código a tu correo.');
      setCode(['', '', '', '', '', '']);
      inputsRef.current[0]?.focus();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al reenviar el código.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex',
      background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #2d2d2d 100%)',
      position: 'relative', overflow: 'hidden'
    }}>
      <Box sx={{
        position: 'absolute', top: '-120px', right: '-120px',
        width: 400, height: 400, borderRadius: '50%',
        background: `radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)`
      }} />

      <Box sx={{ m: 'auto', width: '100%', maxWidth: 440, px: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" sx={{ color: 'white', fontWeight: 800, letterSpacing: '0.12em' }}>INDESAP</Typography>
          <Typography sx={{ color: '#aaaaaa', letterSpacing: '0.22em', fontWeight: 300, fontSize: '0.75rem', mt: 0.5 }}>
            Gestor de Actividades
          </Typography>
        </Box>

        <Card sx={{ borderRadius: 3, background: 'rgba(255,255,255,0.97)', boxShadow: `0 20px 60px rgba(0,0,0,0.3)` }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 700, color: '#111111' }}>
              Verificar Código
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Se envió un código de 6 dígitos a
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#111111', mb: 3 }}>
              {email}
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>}

            {/* OTP Input */}
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', mb: 3 }} onPaste={handlePaste}>
              {code.map((digit, idx) => (
                <Box
                  key={idx}
                  component="input"
                  ref={el => (inputsRef.current[idx] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleChange(idx, e.target.value)}
                  onKeyDown={e => handleKeyDown(idx, e)}
                  autoFocus={idx === 0}
                  sx={{
                    width: 52, height: 60,
                    textAlign: 'center', fontSize: '1.6rem', fontWeight: 700,
                    border: `2px solid ${digit ? '#111111' : '#e0e0e0'}`,
                    borderRadius: 1, outline: 'none', fontFamily: 'Inter, sans-serif',
                    color: '#111111', background: digit ? 'rgba(0,0,0,0.05)' : 'white',
                    transition: 'all 0.15s',
                    '&:focus': { borderColor: '#555555', boxShadow: '0 0 0 3px rgba(0,0,0,0.12)' }
                  }}
                />
              ))}
            </Box>

            <Button
              fullWidth variant="contained" size="large"
              disabled={loading || code.some(d => d === '')}
              onClick={() => handleVerify(code.join(''))}
              sx={{ py: 1.5, fontSize: '1rem', mb: 2 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Verificar Código'}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                ¿No recibiste el código?{' '}
                <Link component="button" onClick={handleResend} disabled={resendLoading} sx={{ fontWeight: 600 }}>
                  {resendLoading ? 'Enviando...' : 'Reenviar'}
                </Link>
              </Typography>
              <Link
                component="button"
                onClick={() => navigate('/login')}
                sx={{ display: 'block', mt: 1.5, fontSize: '0.85rem', color: 'text.secondary' }}
              >
                ← Cambiar correo electrónico
              </Link>
            </Box>
          </CardContent>
        </Card>
      </Box>
      <DevBadge id="page:verificar-otp" />
    </Box>
  );
}
